import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import useDefaultMapConfig from '../hooks/useDefaultMapConfig';

const MapPicker = ({
  isOpen,
  onClose,
  onConfirm,
  initialLocation = null,
  title = 'Select Incident Location',
  activeArea = null,
}) => {
  const { defaultCenter, defaultZoom, mapRestrictions } = useDefaultMapConfig();
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null);

  // Simplified state management
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  // Stable references to prevent unnecessary re-renders
  const stableInitialLocation = useRef(initialLocation);
  const stableActiveArea = useRef(activeArea);

  useEffect(() => {
    stableInitialLocation.current = initialLocation;
  }, [initialLocation]);

  useEffect(() => {
    stableActiveArea.current = activeArea;
  }, [activeArea]);

  const hasSelectedLocation = useCallback(() => {
    return selected !== null;
  }, [selected]);

  const onLoad = useCallback(map => {
    console.log('🔍 MapPicker: Map loaded');
  }, []);

  const onUnmount = useCallback(() => {
    // Map unmounted
  }, []);

  const handleMapClick = useCallback(async event => {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    // Validate coordinates before proceeding
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      console.warn('MapPicker: Invalid coordinates received from map click:', { lat, lng });
      return;
    }

    try {
      // Reverse geocoding to get address
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );

      if (response.ok) {
        const data = await response.json();
        const address = data.display_name || `Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        const location = {
          lat,
          lng,
          coords: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          address,
        };

        setSelected(location);
        selectedRef.current = location;
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      const location = {
        lat,
        lng,
        coords: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        address: `Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      };
      setSelected(location);
      selectedRef.current = location;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (selected) {
      onConfirm(selected);
    }
  }, [selected, onConfirm]);

  const handleClose = useCallback(() => {
    setSelected(null);
    selectedRef.current = null;
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  // Set initial location when modal opens
  useEffect(() => {
    if (isOpen && stableInitialLocation.current && !hasSelectedLocation()) {
      const location = stableInitialLocation.current;

      // Validate that we have valid coordinates
      if (
        location &&
        typeof location.lat === 'number' &&
        typeof location.lng === 'number' &&
        !isNaN(location.lat) &&
        !isNaN(location.lng)
      ) {
        const completeLocation = {
          lat: location.lat,
          lng: location.lng,
          coords: `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
          address: `Coordinates: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
        };
        setSelected(completeLocation);
        selectedRef.current = completeLocation;
        setMapCenter(location);
      } else {
        console.warn('MapPicker: Invalid initial location coordinates:', location);
      }
    }
  }, [isOpen, hasSelectedLocation]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      selectedRef.current = null;
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = event => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  const displayTitle = stableActiveArea.current?.name
    ? `Select Location in ${stableActiveArea.current.name}`
    : 'Select Incident Location';

  const zoomLevel = stableActiveArea.current?.zoom_level || defaultZoom;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="map-picker-modal"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="fixed inset-0 bg-black bg-opacity-50" />
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{displayTitle}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Map Container */}
        <div className="relative h-96">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={zoomLevel}
            onClick={handleMapClick}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: false,
              ...(mapRestrictions?.enabled && {
                restriction: {
                  latLngBounds: mapRestrictions.bounds,
                  strictBounds: mapRestrictions.strictBounds,
                },
              }),
            }}
          >
            {/* Selected location marker */}
            {selected && (
              <Marker position={{ lat: selected.lat, lng: selected.lng }} onLoad={() => {}} />
            )}

            {/* Initial location marker (if different from selected) */}
            {stableInitialLocation.current && !selected && (
              <Marker position={stableInitialLocation.current} onLoad={() => {}} />
            )}
          </GoogleMap>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          {selected && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Selected Location:</p>
              <p className="text-sm font-medium text-gray-900">{selected.address}</p>
              <p className="text-xs text-gray-500">{selected.coords}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selected}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-button"
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPicker;
